import React, { useEffect, useState, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import ReactGA from 'react-ga4';
import { trackJobView, trackCompanyLink, trackJobApply, trackJobSave, trackJobUnsave, trackEvent } from '../../../../utils/analytics/analytics';
import type { AnalyticsEvent } from '../../../../types/analytics-events';
import { ChevronRight, ChevronDown, ChevronUp, Linkedin, Twitter, Facebook, Instagram, Globe, Car, Menu, Copy, Check } from 'lucide-react';
import { localisedRouteGenerator } from '../../../../i18n/routes/routeGenerator';
import { generateJobUrl } from '../../../../utils/routing/generate-url';
import { BASE_URL, DEV_URL, LOCALIZED_PATHS } from '../../../../utils/constants/url-constants';
import { Link } from '../../../../components/Link';
import { BookmarkIcon, ArrowUpOnSquareIcon, MapPinIcon, BriefcaseIcon, WifiIcon, CheckCircleIcon, EyeSlashIcon, EllipsisVerticalIcon, XCircleIcon, BriefcaseIcon as BriefcaseIconOutline } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkIconSolid, CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import { CheckCircleIcon as CheckCircleIconGreen, XCircleIcon as XCircleIconSolid, ArrowUpOnSquareIcon as ArrowUpOnSquareIconSolid, BriefcaseIcon as BriefcaseIconSolid } from '@heroicons/react/24/solid';
import type { DisplayJob } from '../../../../types';
import type { FinalJob } from '../../../../lambda/types';
import { getCompanyLogoSrcset, getCompanyLogoDefaultSrc } from '../../../../utils/company/get-company-logo-url';
import { generateJobPostingSchema } from '../../../../utils/seo/generate-job-posting-schema';
// normalizeJobDetail is now used by useJobNormalization hook
import { generateCanonicalUrls } from '../../../../utils/seo/generate-canonical-urls';
import Skeleton from '../../../../components/Skeleton';
import { useData } from "vike-react/useData";
import { usePageContext } from "vike-react/usePageContext";
import type { Data } from "./+data.js";
import { useAppSelector, useAppDispatch, useAppStore } from '../../../../redux/hooks';
import { ApplicationState, updateJobApplicationState, updateJobHiddenState } from '../../../../redux/features/jobs/jobsSlice';
import { ApplicationStateSelector } from '../../../../components/ApplicationStateSelector';
import { APPLICATION_STATES } from '../../../../components/application-state';
import { useToast } from '../../../../contexts/ToastContext';
import { getIndexName } from '../../../../constants';
import { generateInternalLinks, generateContextualLinks, generateRelatedContentLinks } from '../../../../utils/seo/internal-linking';
import { fixMojibake } from '../../../../utils/text/fix-mojibake';
import { CompanyName } from '../../../../components/CompanyName';
import { useSavedJobActions } from '../../../../hooks/useSavedJobActions';
import { useUserJobStateSync } from '../../../../hooks/useUserJobStateSync';
import { calculateJobMatch } from '../../../../utils/data/job-matching';
import { calculateJobMatchScore } from '../../../../utils/data/calculate-job-match-score';
import { useAuth } from '../../../../hooks/useAuth';
import { MatchDonutWithLoginPrompt } from '../../../../components/MatchDonut';
import { formatDate, calculateEstimatedExpiryDate } from './utils/dateUtils';
import { isDark, isWhiteOrNearWhite, constructApplyUrl, prettifyField } from './utils/jobDetailUtils';
import { transformJobForLanguage, getIconForItem, scrollToSection, normalizeLocationArray, prioritizeLocations, getFirstCompanyUrl, extractYouTubeVideoId, isValidNumber, safeParseNumber, hasAnyValidNumber, getValidNumberOrNull, calculateCompanyAge, formatCompanyAge, getSocialIcon, createLogoErrorHandlerFactory, calculateBrandStyles, generateMetaDescription, generateJobKeywords } from './utils';
import { convertSalaryToMonthlyEUR, hasOriginalSalary, getCurrencySymbol, type OriginalSalary } from './utils/salaryConversion';
import { readChartsCollapseCookie, writeChartsCollapseCookie } from './utils/chartsCollapse';
import { calculateHasAnyCharts } from './utils/chartsUtils';
import { CompanyLogo, JobActionButtons, MatchScoreCard, TabContent, MoneyGraph, MonthlyHiringChart, StarburstChart, SocialMediaButtons, LocationRemotenessChart, SalaryStatisticsChart, SalaryDistributionChart, RoleRoleLevelChart, DonutSunburstChart, CompanyDistributionChart, CompanyLocationMap, LanguageSwitcher, DesktopSidebarTabs, HeroImage, MobileBottomNavigation, JobHeader, JobDetailsGrid, LocationRemotenessSection, CompensationSection, LanguagesSpecialisationsSection, CompanySection, JobDetailsSection, InternalLinksSection, EllipsesMenuDropdown, ApplicationDeadlineSection, MatchScoreBreakdown, ContactEmailSection, OverviewSectionHeader, JobDetailSkeleton, ApplyButton, JobDescriptionSection, JobNotFound404 } from './components';
import { BreadcrumbNavigation } from '../../../../components/BreadcrumbNavigation';
import { isValidSearchReturnPath } from '../../../../utils/routing/validate-search-return-path';
import { sharePage } from './utils/shareUtils';
import { Tooltip } from '../../../../components/Tooltip';
import { useDebugRenderIf, useDebugEffectIf, useDebugStateIf } from '../../../../utils/debug/debug-render';
import { useJobState } from './hooks/useJobState';
import { useJobActions } from './hooks/useJobActions';
import { useJobNormalization } from './hooks/useJobNormalization';
import { useSectionNavigation } from './hooks/useSectionNavigation';
import { useLanguageUrls } from './hooks/useLanguageUrls';
import { useSalaryDisplay } from './hooks/useSalaryDisplay';

// Import the Footer component
import Footer from '../../../../components/Footer';

//TODO:: Fetch object id from all 3 indices so I can generate all 3 urls

/** Read serialized page context from window so first client paint has data and matches server (avoids hydration mismatch). */
function getSerializedData(): Data | undefined {
  if (typeof window === 'undefined') return undefined;
  const pc = (window as { __VITE_PAGE_CONTEXT__?: { data?: Data } }).__VITE_PAGE_CONTEXT__;
  return pc?.data;
}

export interface JobDetailPageProps {
  /** When mounted in the job island (static→interactive swap), data is passed in; no useData. */
  initialData?: Data;
  /** Locale when mounted in island (no usePageContext). */
  initialLocale?: string;
}

const JobDetailPage: React.FC<JobDetailPageProps> = ({ initialData, initialLocale }) => {
  const dataFromHook = useData<Data>();
  const pageContext = usePageContext();
  // Use serialized data on first client paint so we match server output and avoid hydration crash (useData/pageContext can be delayed).
  // When mounted in the job island, initialData is provided.
  const data =
    initialData ??
    dataFromHook ??
    (pageContext as { data?: Data } | undefined)?.data ??
    getSerializedData();
  // Guard: during client-side navigation pageContext can be briefly undefined; avoid reading routeParams until ready.
  // When in island, derive from initialData.job.
  const routeParams = pageContext?.routeParams as { jobId?: string; companyId?: string; seoSlug?: string } | undefined;
  const routeParamsFromJob =
    initialData?.job && typeof initialData.job === 'object' && initialData.job !== null
      ? {
          jobId: String((initialData.job as { objectID?: unknown }).objectID ?? ''),
          companyId: String((initialData.job as { companyId?: unknown }).companyId ?? (initialData.job as { company?: unknown }).company ?? ''),
          seoSlug: (initialData as { seoSlug?: string }).seoSlug ?? '',
        }
      : undefined;
  const effectiveRouteParams = routeParams ?? routeParamsFromJob;
  const seoSlug = effectiveRouteParams?.seoSlug;
  // Safely destructure data - it can be undefined during client-side navigation transitions
  const { job, httpStatusCode, heroImage: serverHeroImage, heroImageHasTransparency, hasAnyCharts: serverHasAnyCharts } = data || {
    job: undefined,
    httpStatusCode: undefined,
    heroImage: undefined,
    heroImageHasTransparency: undefined,
    hasAnyCharts: false,
  };
  const intl = useIntl();

  // Use pageContext.locale for SSR compatibility (available during SSR), fallback to intl.locale for client. In island, use initialLocale.
  const jobLocale = (initialLocale ?? pageContext?.locale ?? intl.locale ?? 'en') as string;

  // Use extracted useJobNormalization hook
  const { displayJob, finalJob, normalizationMeta } = useJobNormalization({
    job,
    jobLocale,
    seoSlug,
  });

  // Charts collapse state - read from cookie on mount (client-side only)
  const [chartsCollapsed, setChartsCollapsed] = useState<boolean>(false);

  useEffect(() => {
    // Read initial state from cookie (client-side only)
    const cookieValue = readChartsCollapseCookie();
    if (cookieValue !== null) {
      setChartsCollapsed(cookieValue);
    }
  }, []);

  const toggleChartsCollapse = () => {
    const newState = !chartsCollapsed;
    setChartsCollapsed(newState);
    writeChartsCollapseCookie(newState);
  };

  // Original job text display state
  const [showOriginalJobText, setShowOriginalJobText] = useState<boolean>(false);

  // Company name copy state
  const [companyNameCopied, setCompanyNameCopied] = useState<boolean>(false);

  // Use extracted useJobState hook
  const { jobId, isSaved, isApplied, isHidden, currentApplicationState, isToolsPage } = useJobState({ displayJob });

  // Get toast and other dependencies
  const { showToast } = useToast();
  const { isAuthenticated, signIn } = useAuth();
  const userProfile = useAppSelector((state) => state.jobForm);
  const comparisonPreferences = useAppSelector((state) => state.comparisonPreferences);
  const userState = useAppSelector((state) => state.userState);
  const { recordViewedJob, setAppliedJobState } = useUserJobStateSync();
  const { saveJob, unsaveJob } = useSavedJobActions();

  // Only calculate if state is hydrated (to avoid SSR/client mismatch)
  const isHydrated = userState.hydratedFromSSR || (typeof window !== 'undefined');

  // Parse returnTo from URL so breadcrumb can link back to previous search (when coming from SRP)
  const breadcrumbSearchReturnHref = useMemo(() => {
    const urlString =
      typeof window !== 'undefined'
        ? window.location.href
        : (pageContext as { urlOriginal?: string })?.urlOriginal ?? '';
    try {
      const url = new URL(urlString, 'http://localhost');
      const raw = url.searchParams.get('returnTo');
      if (raw == null) return null;
      const decoded = decodeURIComponent(raw);
      return isValidSearchReturnPath(decoded) ? decoded : null;
    } catch {
      return null;
    }
  }, [pageContext, initialData]);

  // Generate job URL for share functionality
  const jobUrl = useMemo(() => {
    if (!displayJob) return '';
    const locationStr = Array.isArray(displayJob.location) && displayJob.location.length > 0
      ? String(displayJob.location[0] || '')
      : String(displayJob.location || '');
    return generateJobUrl({
      baseUrl: import.meta.env?.DEV ? DEV_URL : BASE_URL,
      jobId: displayJob.objectID.toString(),
      locale: intl.locale as any,
      title: displayJob.normalisedTitle,
      company: displayJob.companyId ?? '',
      location: locationStr,
      remoteness: String(displayJob.remoteness || ''),
    });
  }, [displayJob, intl.locale]);

  // Use extracted useJobActions hook
  const { handleToggleSaved, handleToggleApplied, handleToggleHide, handleSetApplicationState, handleShare } = useJobActions({
    displayJob,
    jobId,
    isSaved,
    isApplied,
    isHidden,
    showToast,
    url: jobUrl,
  });

  // Tab logic for Benefits, Recruitment, Skills
  const [activeTab, setActiveTab] = useState('Benefits');

  // Ellipses menu state
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen]);

  // Get the state to display in the ellipsis button
  const ellipsisButtonState = React.useMemo(() => {
    if (currentApplicationState) {
      const stateConfig = APPLICATION_STATES.find(s => s.value === currentApplicationState);
      return stateConfig ? {
        label: intl.formatMessage({ id: stateConfig.labelKey, defaultMessage: stateConfig.defaultLabel }),
        icon: stateConfig.iconSolid,
        color: stateConfig.color,
      } : null;
    }
    if (isApplied) {
      return {
        label: intl.formatMessage({ id: 'tools.applicationState.applied', defaultMessage: 'Applied', description: 'Application state label indicating job has been applied to' }),
        icon: CheckCircleIconSolid,
        color: 'blue',
      };
    }
    return null;
  }, [currentApplicationState, isApplied, intl]);

  // Refs for scrolling to sections
  const overviewRef = React.useRef<HTMLDivElement>(null);
  const companyRef = React.useRef<HTMLDivElement>(null);
  const jobDetailsRef = React.useRef<HTMLDivElement>(null);

  // Use section navigation hook
  const { activeSection, handleScrollToSection } = useSectionNavigation({
    overviewRef,
    companyRef,
    jobDetailsRef,
    displayJob,
  });

  // Debug: Track renders and navigation state
  useDebugRenderIf('JobDetailPage', {
    jobId: displayJob?.objectID,
    activeSection,
    hasJob: !!displayJob,
    locale: jobLocale,
  });

  // Debug: Track activeSection state changes
  useDebugStateIf('activeSection', activeSection);

  // Calculate match score data once
  const matchScoreData = React.useMemo(() => {
    if (!displayJob || !isAuthenticated || !isHydrated) return null;
    return calculateJobMatchScore(displayJob, userProfile, comparisonPreferences);
  }, [displayJob, isAuthenticated, isHydrated, userProfile, comparisonPreferences]);

  // Create save handler
  const handleSave = React.useCallback(async () => {
    if (!displayJob) return;
    const payload = { ...displayJob, id: displayJob.objectID };
    if (!isSaved) {
      trackJobSave({
        jobId: displayJob.objectID,
        jobTitle: displayJob.normalisedTitle || displayJob.title || '',
        locale: intl.locale,
      });
      const success = await saveJob(payload);
      if (success) {
        showToast(
          intl.formatMessage({ id: 'hit.toast.saved', defaultMessage: 'Job saved', description: 'Success toast notification message shown when a job is saved' }),
          'success'
        );
      }
    } else {
      trackJobUnsave({
        jobId: displayJob.objectID,
        jobTitle: displayJob.normalisedTitle || displayJob.title || '',
        locale: intl.locale,
      });
      const success = await unsaveJob(payload);
      if (success) {
        showToast(
          intl.formatMessage({ id: 'hit.toast.unsaved', defaultMessage: 'Job removed from saved', description: 'Info toast notification message shown when a job is removed from saved jobs' }),
          'info'
        );
      }
    }
  }, [displayJob, isSaved, saveJob, unsaveJob, showToast, intl]);

  // const routerLocation = useLocation(); // Commented out react-router-dom
  // Get sendEvent from location state if passed from Hit
  // const sendEvent = routerLocation.state?.sendEvent; // Commented out

  const [showContactEmail, setShowContactEmail] = useState(false);
  const [showCompanyContactDetails, setShowCompanyContactDetails] = useState(false);
  const [showCompanyLocationMap, setShowCompanyLocationMap] = useState(false);
  const [companyInfoActiveTab, setCompanyInfoActiveTab] = useState<
    'financial' | 'hiring' | 'roles' | 'locations' | 'languages' | 'map'
  >('financial');
  const [showOriginalSalary, setShowOriginalSalary] = useState(false);
  const [compensationExpanded, setCompensationExpanded] = useState(true);
  // Remount salary charts after a short delay so they paint in the visible container (island swap).
  const [salaryChartRemountKey, setSalaryChartRemountKey] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setSalaryChartRemountKey((k) => k + 1), 150);
    return () => clearTimeout(t);
  }, []);

  // Calculate estimated expiry date if applicationDeadline is missing (35 days from postedDate)
  // This hook must be called before any early returns to follow Rules of Hooks
  const estimatedExpiryDate = React.useMemo(() => {
    if (!displayJob) return null;
    return calculateEstimatedExpiryDate(displayJob.applicationDeadline, displayJob.postedDate);
  }, [displayJob]);

  // Track salary insight view (once per job)
  const salaryInsightTrackedRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (displayJob?.objectID && displayJob.objectID !== salaryInsightTrackedRef.current) {
      const hasSalaryInsight =
        typeof displayJob.salaryRangeMin === 'number' &&
        typeof displayJob.salaryRangeMax === 'number' &&
        displayJob.salaryRangeMin > 0 &&
        displayJob.salaryRangeMax > 0;

      if (hasSalaryInsight) {
        // Determine if salary is exact (from job posting) or estimated
        const hasExactSalary = !!displayJob.salaryDetails;
        const hasEstimatedSalary = !hasExactSalary;

        trackEvent({
          name: 'salary_insight_view',
          params: {
            job_id: displayJob.objectID,
            company_id: displayJob.companyId || '',
            has_exact_salary: hasExactSalary,
            has_estimated_salary: hasEstimatedSalary,
            locale: intl.locale,
          },
        });

        salaryInsightTrackedRef.current = displayJob.objectID;
      }
    }
  }, [displayJob, intl.locale]);

  // Effect to handle side effects (tracking, etc.) - only run once per job
  useEffect(() => {
    // Handle side effects (tracking, etc.) - only run once per job
    if (job?.objectID && displayJob) {
      void recordViewedJob({ id: job.objectID, ...displayJob });

      // Track job view with typed event
      const hasSalaryInsight =
        typeof displayJob.salaryRangeMin === 'number' &&
        typeof displayJob.salaryRangeMax === 'number' &&
        displayJob.salaryRangeMin > 0 &&
        displayJob.salaryRangeMax > 0;

      const locationLabel = Array.isArray(displayJob.location) && displayJob.location.length > 0
        ? displayJob.location[0]
        : (typeof displayJob.location === 'string' ? displayJob.location : undefined);

      trackEvent({
        name: 'job_view',
        params: {
          job_id: job.objectID,
          job_title: displayJob.normalisedTitle || displayJob.title || '',
          company_id: displayJob.companyId || '',
          location_label: locationLabel,
          is_remote: displayJob.remoteness === 'remote' || displayJob.remoteness === 'hybrid',
          has_salary_insight: hasSalaryInsight,
          locale: intl.locale,
        },
      });
    }
  }, [displayJob, job?.objectID, recordViewedJob, intl.locale]);


  // No data from useData() or pageContext yet: show skeleton only when we're on a job route and have nothing to show.
  const hasData = data != null;
  const hasJob = job != null && job !== undefined;
  if (!hasData && effectiveRouteParams?.jobId) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <JobDetailSkeleton />
      </div>
    );
  }

  // Data has loaded but job is missing: show not found only when we're sure (data resolved, no job).
  if (hasData && !hasJob) {
    return <JobNotFound404 />;
  }

  // Still waiting for displayJob (normalization), or job present but displayJob null — show skeleton so client never renders minimal div and mismatches server.
  if (hasJob && (displayJob === undefined || displayJob === null)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <JobDetailSkeleton />
      </div>
    );
  }

  if (!displayJob) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <JobDetailSkeleton />
      </div>
    );
  }

  const {
    title,
    description,
    postedDate,
    jobType,
    location,
    remoteness,
    companyId,
    requiredLanguages,
    optionalLanguages,
    salaryRangeMin,
    salaryRangeMax,
    salaryPeriod,
    salaryDetails,
    currency,
    peopleManagementTier,
    specialisations,
    applicationDeadline,
    contactEmail,
    url,
    normalisedRoleLevel,
    normalisedTitle,
    brandColour,
    companyIndustry,
    employerOverview,
    companyHQInFinland,
    companyCoreValues,
    companyAwardsAndCertifications,
    companyContactDetails,
    companyHeadCountMin,
    companyHeadCountMax,
    companyHeadcountRangeMin,
    companyHeadcountRangeMax,
    companyAnnualRevenueRangeMin,
    companyAnnualRevenueRangeMax,
    officeImages,
    socialMediaLinks,
    vacationWeeks,
    minimumYearsOfExperience,
    perks,
    visaSponsor,
    // Additional company fields (using new prefixed names to avoid collisions)
    companyRevenue,
    companyProfit,
    companyProfitMargin,
    companyVideo,
    linkedinUrl,
    facebookUrl,
    instagramUrl,
    youtubeUrl,
    tiktokUrl,
    registryMainBusinessLineDescriptionEn,
    registryMainBusinessLineDescriptionFi,
    registryMainBusinessLineDescriptionSv,
    registryStreet,
    registryCity,
    registryPostCode,
    registryBuildingNumber,
    address, // Address field from Algolia
    address_fi,
    address_sv,
    companyAbout,
    companyMonthlyCounts,
    companyHiringTrends,
    companyHiringTrendsByLocale,
    locationSalaryStats,
    businessID,
    registryBusinessId,
    registryRegistrationDate,
    originalJobText,
    originalJobLanguage,
    companyGeocoordinates,
  } = displayJob as DisplayJob;

  // Use salary display hook
  const { displaySalary, shouldShowOriginalButton } = useSalaryDisplay({
    displayJob,
    showOriginalSalary,
  });

  // Get trends for current locale, fallback to English if not available
  const currentLocaleTrends = companyHiringTrendsByLocale?.[jobLocale as 'en' | 'fi' | 'sv']
    || companyHiringTrendsByLocale?.en
    || companyHiringTrends;

  // Use server-side calculated hasAnyCharts to prevent CLS (Cumulative Layout Shift)
  // This value is calculated in +data.ts before the page renders. In the job island, serialized data
  // might omit it, so fallback to computing from job so charts always show in the final version.
  const hasAnyCharts =
    serverHasAnyCharts ??
    (job && typeof job === 'object'
      ? calculateHasAnyCharts(job as Parameters<typeof calculateHasAnyCharts>[0], jobLocale)
      : false);

  // Set default tab based on available data
  useEffect(() => {
    if (!chartsCollapsed) {
      const hasFinancial = hasAnyValidNumber(companyRevenue, companyProfit, companyProfitMargin);
      const hasHiring = !!(companyMonthlyCounts && Object.keys(companyMonthlyCounts).length > 0);
      const hasLocations = !!(currentLocaleTrends &&
        ((currentLocaleTrends.locationDistribution &&
          Object.keys(currentLocaleTrends.locationDistribution).length > 0 &&
          Object.values(currentLocaleTrends.locationDistribution).some(val => hasAnyValidNumber(val))) ||
          (currentLocaleTrends.remotenessDistribution &&
            (currentLocaleTrends.remotenessDistribution.remote > 0 ||
              currentLocaleTrends.remotenessDistribution.hybrid > 0 ||
              currentLocaleTrends.remotenessDistribution.onsite > 0))));
      const hasRoles = !!(currentLocaleTrends && (currentLocaleTrends.roleDistribution || currentLocaleTrends.roleLevelDistribution));

      // Check if current tab has data, if not switch to first available
      const currentTabHasData =
        (companyInfoActiveTab === 'financial' && hasFinancial) ||
        (companyInfoActiveTab === 'hiring' && hasHiring) ||
        (companyInfoActiveTab === 'roles' && hasRoles) ||
        (companyInfoActiveTab === 'locations' && hasLocations) ||
        (companyInfoActiveTab === 'languages' && !!(currentLocaleTrends?.languageRequirements &&
          Object.keys(currentLocaleTrends.languageRequirements).length > 0 &&
          Object.values(currentLocaleTrends.languageRequirements).some(val => hasAnyValidNumber(val)))) ||
        (companyInfoActiveTab === 'map' && !!companyGeocoordinates);

      if (!currentTabHasData) {
        if (hasFinancial) {
          setCompanyInfoActiveTab('financial');
        } else if (hasHiring) {
          setCompanyInfoActiveTab('hiring');
        } else if (hasLocations) {
          setCompanyInfoActiveTab('locations');
        } else if (hasRoles) {
          setCompanyInfoActiveTab('roles');
        }
      }
    }
  }, [chartsCollapsed, companyRevenue, companyProfit, companyProfitMargin, companyMonthlyCounts, currentLocaleTrends, companyGeocoordinates, companyInfoActiveTab]);

  // Use server-validated heroImage (only shown if it has optimal dimensions)
  const heroImage = serverHeroImage;

  // Construct applyUrl with UTM params for the apply button
  // For Tyomarkkinatori jobs, swap locale (fi -> en) when viewing English version
  const applyUrl = constructApplyUrl(url, companyId, jobLocale);

  const canonicalLocale = intl.locale === 'fi' || intl.locale === 'sv' ? intl.locale : 'en';
  const canonicalSource = normalizationMeta?.canonicalJob || finalJob;

  // Use language URLs hook
  const languageUrls = useLanguageUrls({
    canonicalSource,
    canonicalLocale,
  });

  const canonicalUrlForSchema = canonicalSource
    ? languageUrls?.current
    : (typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : undefined);

  // Generate schema and badge status
  const { schema: structuredData, isContentComplete, missingFields } = generateJobPostingSchema(
    displayJob,
    intl.locale || 'en',
    applyUrl,
    canonicalUrlForSchema,
    normalizationMeta?.schemaOverrides ?? {}
  );

  // Prettify missing fields for tooltip
  const missingTooltip = ['Missing fields:', ...missingFields.map(prettifyField)];

  const formattedPostedDate = formatDate(postedDate, intl.locale);
  const formattedApplicationDate = formatDate(applicationDeadline, intl.locale);
  const formattedEstimatedExpiryDate = estimatedExpiryDate ? formatDate(estimatedExpiryDate, intl.locale) : null;

  // Generate meta description and keywords
  const metaDesc = generateMetaDescription(description);
  const jobKeywords = generateJobKeywords(
    intl.locale as 'fi' | 'en' | 'sv',
    title,
    companyId,
    location,
    companyIndustry,
    specialisations
  );

  // Calculate brand styles
  const brandStyles = React.useMemo(() => {
    return calculateBrandStyles({
      brandColour: brandColour,
      isTyomarkkinatori: displayJob?.isTyomarkkinatori,
    });
  }, [brandColour, displayJob?.isTyomarkkinatori]);

  const {
    effectiveBrandColour,
    useBrandColor,
    leftBorderStyle,
    applyBtnStyle,
    applyBtnHoverStyle,
    activeTabStyle,
    savedIconStyle,
  } = brandStyles;

  // Create logo error handler factory
  const createLogoErrorHandler = React.useMemo(() => createLogoErrorHandlerFactory(), []);

  // Generate comprehensive internal links using our new utility
  const internalLinks = generateInternalLinks(displayJob, intl.locale);
  const contextualLinks = generateContextualLinks(displayJob, intl.locale);
  const contentLinks = generateRelatedContentLinks(displayJob, intl.locale);

  // Combine all links and sort by priority
  const allLinks = [...internalLinks, ...contextualLinks, ...contentLinks]
    .sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    })
    .slice(0, 8); // Limit to top 8 links to avoid overwhelming


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900" suppressHydrationWarning>
      <div className="max-w-6xl mx-auto p-2 pt-2 pb-6 sm:p-6">
            <BreadcrumbNavigation
              showHome={false}
              items={[
                {
                  label: intl.formatMessage({
                    id: 'breadcrumb.jobs',
                    defaultMessage: 'Jobs',
                    description: 'Breadcrumb link back to search results',
                  }),
                  href: breadcrumbSearchReturnHref ?? (jobLocale === 'en' ? '/' : `/${jobLocale}`),
                },
                { label: title },
              ]}
              trackHomeClick={true}
              trackData={{ jobId: displayJob?.objectID }}
            />

            {languageUrls && (
              <LanguageSwitcher
                languageUrls={languageUrls}
                canonicalLocale={canonicalLocale}
                originalJobLanguage={originalJobLanguage}
              />
            )}

            {/* Main content area - core job entity block */}
            <main id="job-main-content" itemScope itemType="https://schema.org/JobPosting">
              {/* Desktop: Tabs on the left side (sticky) */}
              <div className="hidden lg:flex gap-6">
                <DesktopSidebarTabs
                  activeSection={activeSection}
                  onSectionChange={handleScrollToSection}
                  brandColor={effectiveBrandColour}
                  useBrandColor={useBrandColor}
                />
                <div className="flex-1 min-w-0">
                  {/* Single Card with All Sections - iOS26 Style */}
                  <div
                    className={
                      'mt-6 ' +
                      'bg-white/90 dark:bg-gray-900/90 shadow border border-gray-200/50 dark:border-gray-700/50 rounded-[28px] px-4 md:px-10 py-10 space-y-6'
                    }
                    style={leftBorderStyle}
                  >
                    <HeroImage
                      heroImage={heroImage}
                      heroImageHasTransparency={heroImageHasTransparency}
                      title={title}
                    />

                    {/* SECTION 1: Overview */}
                    <div ref={overviewRef} data-section="overview" className="scroll-mt-20" id="overview">
                      <OverviewSectionHeader
                        displayJob={displayJob}
                        isAuthenticated={isAuthenticated}
                        isHydrated={isHydrated}
                        matchScoreData={matchScoreData}
                        onLoginClick={() => signIn()}
                        isSaved={isSaved}
                        isApplied={isApplied}
                        isHidden={isHidden}
                        currentApplicationState={currentApplicationState}
                        ellipsisButtonState={ellipsisButtonState}
                        isMenuOpen={isMenuOpen}
                        menuRef={menuRef}
                        effectiveBrandColour={effectiveBrandColour}
                        useBrandColor={useBrandColor}
                        onSave={handleSave}
                        onToggleHide={handleToggleHide}
                        onShare={handleShare}
                        onSetApplicationState={handleSetApplicationState}
                        onToggleApplied={handleToggleApplied}
                        onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
                      />

                      <JobHeader
                        title={title}
                        normalisedTitle={normalisedTitle}
                        companyId={companyId}
                        location={location}
                        isTyomarkkinatori={displayJob?.isTyomarkkinatori}
                        onLogoError={createLogoErrorHandler}
                      />

                      {displaySalary && (
                        <CompensationSection
                          displaySalary={displaySalary}
                          compensationExpanded={compensationExpanded}
                          onToggleExpanded={() => setCompensationExpanded(!compensationExpanded)}
                          showOriginalSalary={showOriginalSalary}
                          shouldShowOriginalButton={shouldShowOriginalButton}
                          onToggleShowOriginal={() => setShowOriginalSalary(!showOriginalSalary)}
                          salaryDetails={salaryDetails}
                          vacationWeeks={vacationWeeks}
                          displayJob={displayJob}
                          salaryRangeMin={salaryRangeMin}
                          salaryRangeMax={salaryRangeMax}
                          currency={currency}
                          salaryPeriod={salaryPeriod}
                          jobLocale={jobLocale}
                          location={location}
                          chartRemountKey={salaryChartRemountKey}
                          normalisedTitle={normalisedTitle}
                          title={title}
                          normalisedRoleLevel={normalisedRoleLevel}
                          onViewBenefits={() => {
                            setActiveTab('Benefits');
                            scrollToSection('jobDetails-benefits', 20);
                          }}
                        />
                      )}

                      {/* Salary Distribution Chart for jobs without salary range */}
                      {(!salaryRangeMin || !salaryRangeMax || salaryRangeMin <= 0 || salaryRangeMax <= 0) &&
                        (displayJob?.allSalaryStats || displayJob?.companySalaryStats || displayJob?.salaryStats || displayJob?.locationSalaryStats) && (
                          <div className="mb-6 p-5 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-800 shadow-lg">
                            <SalaryDistributionChart
                              key={salaryChartRemountKey}
                              salaryStats={displayJob.companySalaryStats || displayJob.salaryStats}
                              locationSalaryStats={displayJob.locationSalaryStats}
                              allSalaryStats={displayJob.allSalaryStats}
                              jobSalaryMin={salaryRangeMin}
                              jobSalaryMax={salaryRangeMax}
                              currency={currency}
                              salaryPeriod={salaryPeriod}
                              locale={jobLocale}
                              jobLocation={location}
                              jobTitle={normalisedTitle || title}
                            />
                          </div>
                        )}

                      <LocationRemotenessSection
                        location={location}
                        remoteness={remoteness}
                      />

                      {/* Application Deadline / Estimated Expiry */}
                      <ApplicationDeadlineSection
                        formattedApplicationDate={formattedApplicationDate}
                        formattedEstimatedExpiryDate={formattedEstimatedExpiryDate}
                        formattedPostedDate={formattedPostedDate}
                      />

                      <JobDetailsGrid
                        normalisedRoleLevel={normalisedRoleLevel}
                        peopleManagementTier={peopleManagementTier}
                        jobType={jobType}
                        minimumYearsOfExperience={minimumYearsOfExperience}
                        visaSponsor={visaSponsor}
                      />

                      {/* Match Score Details */}
                      {isAuthenticated && displayJob && (() => {
                        const matchScore = calculateJobMatch(displayJob, userProfile);
                        if (!matchScore) return null;
                        return <MatchScoreBreakdown matchScore={matchScore} />;
                      })()}

                      <LanguagesSpecialisationsSection
                        requiredLanguages={requiredLanguages}
                        optionalLanguages={optionalLanguages}
                        specialisations={specialisations}
                      />

                      {/* Job Description */}
                      <JobDescriptionSection description={description} />

                      {/* Apply Button */}
                      <ApplyButton
                        applyUrl={applyUrl}
                        isApplied={isApplied}
                        displayJob={displayJob}
                        applyBtnStyle={applyBtnStyle}
                        applyBtnHoverStyle={applyBtnHoverStyle}
                        effectiveBrandColour={effectiveBrandColour}
                        onSetApplied={setAppliedJobState}
                      />

                      {/* Contact Email Link */}
                      <ContactEmailSection
                        contactEmail={contactEmail}
                        showContactEmail={showContactEmail}
                        onShowContactEmail={() => setShowContactEmail(true)}
                      />
                    </div>

                    <CompanySection
                      displayJob={displayJob}
                      companyId={companyId}
                      companyIndustry={companyIndustry}
                      companyAbout={companyAbout}
                      companyContactDetails={companyContactDetails}
                      companyRevenue={companyRevenue}
                      companyProfit={companyProfit}
                      companyProfitMargin={companyProfitMargin}
                      companyMonthlyCounts={companyMonthlyCounts}
                      companyHeadCountMin={companyHeadCountMin}
                      companyHeadCountMax={companyHeadCountMax}
                      companyHeadcountRangeMin={companyHeadcountRangeMin}
                      companyHeadcountRangeMax={companyHeadcountRangeMax}
                      companyHQInFinland={companyHQInFinland}
                      companyVideo={companyVideo}
                      linkedinUrl={linkedinUrl}
                      facebookUrl={facebookUrl}
                      instagramUrl={instagramUrl}
                      youtubeUrl={youtubeUrl}
                      tiktokUrl={tiktokUrl}
                      registryMainBusinessLineDescriptionEn={registryMainBusinessLineDescriptionEn}
                      registryMainBusinessLineDescriptionFi={registryMainBusinessLineDescriptionFi}
                      registryMainBusinessLineDescriptionSv={registryMainBusinessLineDescriptionSv}
                      registryStreet={registryStreet}
                      registryCity={registryCity}
                      registryPostCode={registryPostCode}
                      registryBuildingNumber={registryBuildingNumber}
                      address={address}
                      address_fi={address_fi}
                      address_sv={address_sv}
                      businessID={businessID}
                      registryBusinessId={registryBusinessId}
                      companyGeocoordinates={companyGeocoordinates}
                      currentLocaleTrends={currentLocaleTrends}
                      jobLocale={jobLocale}
                      brandColour={effectiveBrandColour}
                      useBrandColor={useBrandColor}
                      hasAnyCharts={hasAnyCharts}
                      chartsCollapsed={chartsCollapsed}
                      onToggleChartsCollapse={toggleChartsCollapse}
                      companyRef={companyRef}
                    />

                    <JobDetailsSection
                      displayJob={displayJob}
                      specialisations={specialisations}
                      optionalLanguages={optionalLanguages}
                      perks={perks}
                      originalJobText={originalJobText}
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                      activeTabStyle={activeTabStyle}
                      jobDetailsRef={jobDetailsRef}
                    />

                    {/* Related Opportunities Section - COMMENTED OUT FOR PERFORMANCE TESTING */}
                    {/* {allLinks.length > 0 && (
                      <InternalLinksSection links={allLinks} />
                    )} */}
                  </div>
                </div>
              </div>

              {/* Mobile: Card without left sidebar (tabs will be in bottom nav) - iOS26 Style */}
              <div className="lg:hidden">
                <div
                  className={
                    'mt-6 ' +
                    'bg-white/90 dark:bg-gray-900/90 shadow rounded-[28px] px-4 md:px-10 py-10 space-y-6 transition-colors duration-300 border border-gray-200/50 dark:border-gray-700/50 overflow-hidden'
                  }
                  style={leftBorderStyle}
                >
                  <HeroImage
                    heroImage={heroImage}
                    heroImageHasTransparency={heroImageHasTransparency}
                    title={title}
                  />

                  {/* SECTION 1: Overview */}
                  <div ref={overviewRef} data-section="overview" className="scroll-mt-20" id="overview">
                    <OverviewSectionHeader
                      displayJob={displayJob}
                      isAuthenticated={isAuthenticated}
                      isHydrated={isHydrated}
                      matchScoreData={matchScoreData}
                      onLoginClick={() => signIn()}
                      isSaved={isSaved}
                      isApplied={isApplied}
                      isHidden={isHidden}
                      currentApplicationState={currentApplicationState}
                      ellipsisButtonState={ellipsisButtonState}
                      isMenuOpen={isMenuOpen}
                      menuRef={menuRef}
                      effectiveBrandColour={effectiveBrandColour}
                      useBrandColor={useBrandColor}
                      onSave={handleSave}
                      onToggleHide={handleToggleHide}
                      onShare={handleShare}
                      onSetApplicationState={handleSetApplicationState}
                      onToggleApplied={handleToggleApplied}
                      onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
                    />

                    {/* Header Row: Role Title with Company Logo - Removed duplicate h1 to fix SEO issue (h1 already exists in desktop layout) */}
                    <div className="flex flex-col mb-6 gap-2">
                      {title ? (
                        <div className="flex items-center gap-3">
                          {/* Small Company Logo */}
                          {companyId && (
                            <div className="flex-shrink-0">
                              {(() => {
                                const isTyomarkkinatori = displayJob?.isTyomarkkinatori ?? false;
                                return (
                                  <div
                                    className={`w-10 h-10 flex items-center justify-center ${isTyomarkkinatori ? '' : 'bg-white dark:bg-gray-800 border border-gray-200/60 dark:border-gray-700/60'} shadow-sm rounded-xl overflow-hidden p-1`}
                                    style={isTyomarkkinatori ? { backgroundColor: '#6B46C1' } : {}}
                                  >
                                    <img
                                      className="object-contain w-full h-full"
                                      alt={`${companyId} company logo`}
                                      src={getCompanyLogoDefaultSrc(companyId, isTyomarkkinatori)}
                                      srcSet={getCompanyLogoSrcset(companyId, isTyomarkkinatori)}
                                      sizes="40px"
                                      onError={createLogoErrorHandler(companyId)}
                                      loading="eager"
                                      decoding="async"
                                    />
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            {(() => {
                              const primaryLocation = Array.isArray(location)
                                ? (location[0] || '')
                                : (location || '');
                              const titleText = [title, companyId, primaryLocation]
                                .filter(Boolean)
                                .join(' - ');
                              return (
                                <div className="truncate font-bold text-xl text-gray-900 dark:text-white">{fixMojibake(titleText)}</div>
                              );
                            })()}
                            {normalisedTitle && normalisedTitle !== title ? (
                              <div className="truncate text-sm text-gray-500 dark:text-gray-400 font-normal italic mt-1" title={fixMojibake(normalisedTitle ?? '')}>
                                {fixMojibake(normalisedTitle ?? '')}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </div>

                    {/* Prominent Compensation Display */}
                    {displaySalary && (
                      <div className="mb-6 p-5 rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-800 shadow-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="text-xs font-semibold text-green-700 dark:text-green-300 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.compensation" defaultMessage="Compensation" description="Section label for job compensation information (salary, benefits, vacation)" />
                            </div>
                            <button
                              type="button"
                              onClick={() => setCompensationExpanded(!compensationExpanded)}
                              className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-transform cursor-pointer"
                              aria-label={compensationExpanded
                                ? intl.formatMessage({ id: 'jobDetailPage.collapseCompensation', defaultMessage: 'Collapse compensation details' })
                                : intl.formatMessage({ id: 'jobDetailPage.expandCompensation', defaultMessage: 'Expand compensation details' })}
                            >
                              {compensationExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          {shouldShowOriginalButton && (
                            <button
                              type="button"
                              onClick={() => setShowOriginalSalary(!showOriginalSalary)}
                              className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 underline cursor-pointer"
                            >
                              {showOriginalSalary ? (
                                <FormattedMessage id="jobDetailPage.showConverted" defaultMessage="Show EUR/month" description="Button label to display salary converted to EUR per month" />
                              ) : (
                                <FormattedMessage id="jobDetailPage.seeOriginal" defaultMessage="See original" description="Button label to view original salary amount before conversion" />
                              )}
                            </button>
                          )}
                        </div>
                        <div className="text-3xl font-bold text-green-900 dark:text-green-100">
                          {getCurrencySymbol(displaySalary.currency)}{displaySalary.min.toLocaleString()} - {getCurrencySymbol(displaySalary.currency)}{displaySalary.max.toLocaleString()}
                          <span className="text-lg font-normal text-green-700 dark:text-green-300 ml-2">
                            /{displaySalary.period === 'annual'
                              ? intl.formatMessage({ id: 'jobDetailPage.period.year', defaultMessage: 'year', description: 'Time period unit for salary (per year)' })
                              : displaySalary.period === 'monthly'
                                ? intl.formatMessage({ id: 'jobDetailPage.period.month', defaultMessage: 'month', description: 'Time period unit for salary (per month)' })
                                : displaySalary.period === 'daily'
                                  ? intl.formatMessage({ id: 'jobDetailPage.period.day', defaultMessage: 'day', description: 'Time period unit for salary (per day)' })
                                  : intl.formatMessage({ id: 'jobDetailPage.period.hour', defaultMessage: 'hour', description: 'Time period unit for salary (per hour)' })}
                          </span>
                        </div>
                        {compensationExpanded && (
                          <>
                            {salaryDetails && (
                              <div className="text-sm text-green-700 dark:text-green-300 mt-2 italic">
                                {fixMojibake(salaryDetails)}
                              </div>
                            )}
                            {vacationWeeks != null && vacationWeeks > 0 && (
                              <div className="text-xs text-green-600 dark:text-green-400 mt-2">
                                <FormattedMessage
                                  id="jobDetailPage.vacationWeeks"
                                  defaultMessage="Vacation: {weeks} weeks"
                                  description="Label showing number of vacation weeks offered, where {weeks} is the number"
                                  values={{ weeks: vacationWeeks }}
                                />
                              </div>
                            )}
                            {displayJob?.benefits && displayJob.benefits.length > 0 && (
                              <button
                                onClick={() => {
                                  setActiveTab('Benefits');
                                  scrollToSection('jobDetails-benefits', 20);
                                }}
                                className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 underline mt-2 cursor-pointer"
                              >
                                <FormattedMessage id="jobDetailPage.viewBenefits" defaultMessage="View benefits" description="Button label to navigate to benefits section on job detail page" />
                              </button>
                            )}
                            {/* Salary Distribution Chart */}
                            {(displayJob?.allSalaryStats || displayJob?.companySalaryStats || displayJob?.salaryStats || displayJob?.locationSalaryStats) && (
                              <SalaryDistributionChart
                                key={salaryChartRemountKey}
                                salaryStats={displayJob.companySalaryStats || displayJob.salaryStats}
                                locationSalaryStats={displayJob.locationSalaryStats}
                                allSalaryStats={displayJob.allSalaryStats}
                                jobSalaryMin={salaryRangeMin}
                                jobSalaryMax={salaryRangeMax}
                                currency={currency}
                                salaryPeriod={salaryPeriod}
                                locale={jobLocale}
                                jobLocation={location}
                                jobTitle={normalisedTitle || title}
                              />
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Salary Distribution Chart for jobs without salary range */}
                    {(!salaryRangeMin || !salaryRangeMax || salaryRangeMin <= 0 || salaryRangeMax <= 0) &&
                      (displayJob?.allSalaryStats || displayJob?.companySalaryStats || displayJob?.salaryStats || displayJob?.locationSalaryStats) && (
                        <div className="mb-6 p-5 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-800 shadow-lg">
                          <SalaryDistributionChart
                            key={salaryChartRemountKey}
                            salaryStats={displayJob.companySalaryStats || displayJob.salaryStats}
                            locationSalaryStats={displayJob.locationSalaryStats}
                            allSalaryStats={displayJob.allSalaryStats}
                            jobSalaryMin={salaryRangeMin}
                            jobSalaryMax={salaryRangeMax}
                            currency={currency}
                            salaryPeriod={salaryPeriod}
                            locale={jobLocale}
                            jobLocation={location}
                            jobTitle={normalisedTitle || title}
                          />
                        </div>
                      )}

                    {/* Prominent Location and Remoteness Display */}
                    <div className="mb-6">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        {/* Location */}
                        <div className="p-4 rounded-2xl bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 shadow-md">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                              <MapPinIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1 uppercase tracking-wide">
                                <FormattedMessage id="jobDetailPage.location" defaultMessage="Location" description="Section label for job location information" />
                              </div>
                              {(() => {
                                // Prioritize locations that include Finland, Remote, Global, Remote - Europe, Remote - EMEA, etc.
                                const priorityKeywords = [
                                  // Finland
                                  'finland', 'suomi', 'finländ',
                                  // Remote variations
                                  'remote', 'etätyö', 'etä', 'distans', 'fjärr',
                                  // Global
                                  'global', 'maailmanlaajuinen', 'världsomfattande',
                                  // Remote - Europe variations
                                  'remote - europe', 'etätyö - eurooppa', 'etä - eurooppa', 'distans - europa', 'fjärr - europa',
                                  // Remote - EMEA variations
                                  'remote - emea', 'etätyö - emea', 'etä - emea', 'distans - emea', 'fjärr - emea',
                                ];

                                const locationsArray: string[] = (() => {
                                  if (Array.isArray(location)) {
                                    return location.filter((loc): loc is string => typeof loc === 'string' && loc.trim() !== '');
                                  }
                                  const locStr = location as string | undefined;
                                  if (typeof locStr === 'string' && locStr.trim() !== '') {
                                    return [locStr];
                                  }
                                  return [];
                                })();

                                if (locationsArray.length === 0) {
                                  return (
                                    <span className="text-blue-600 dark:text-blue-400 italic">
                                      <FormattedMessage id="jobDetailPage.noLocation" defaultMessage="No location" description="Fallback text when job location is not available" />
                                    </span>
                                  );
                                }

                                // Separate prioritized and other locations
                                const prioritizedLocations: string[] = [];
                                const otherLocations: string[] = [];

                                locationsArray.forEach((loc) => {
                                  const cleanLoc = (loc || '').replace(/,\s*Finland$/, '').trim();
                                  if (!cleanLoc) return;

                                  const lowerLoc = cleanLoc.toLowerCase();
                                  const isPriority = priorityKeywords.some(keyword => lowerLoc.includes(keyword));

                                  if (isPriority) {
                                    prioritizedLocations.push(cleanLoc);
                                  } else {
                                    otherLocations.push(cleanLoc);
                                  }
                                });

                                // Combine: prioritized first, then others
                                const primaryLocations = prioritizedLocations.length > 0 ? prioritizedLocations : otherLocations;
                                const subtitleLocations = prioritizedLocations.length > 0 ? otherLocations : [];

                                return (
                                  <>
                                    {primaryLocations.length > 0 && (
                                      <div className="text-lg font-bold text-blue-900 dark:text-blue-100">
                                        {primaryLocations.map((loc, idx) => (
                                          <span key={loc + idx}>
                                            {fixMojibake(loc)}
                                            {idx < primaryLocations.length - 1 && ', '}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    {subtitleLocations.length > 0 && (
                                      <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                        {subtitleLocations.map((loc, idx) => (
                                          <span key={loc + idx}>
                                            {fixMojibake(loc)}
                                            {idx < subtitleLocations.length - 1 && ', '}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>

                        {/* Remoteness */}
                        <div className="p-4 rounded-2xl bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-200 dark:border-purple-800 shadow-md">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
                              <WifiIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold text-purple-700 dark:text-purple-300 mb-1 uppercase tracking-wide">
                                <FormattedMessage id="jobDetailPage.remoteness" defaultMessage="Remoteness" description="Section label for work model/remoteness information (remote, hybrid, onsite)" />
                              </div>
                              <div className="text-lg font-bold text-purple-900 dark:text-purple-100 truncate">
                                {remoteness ? (
                                  fixMojibake(remoteness ?? '')
                                ) : (
                                  <span className="text-purple-600 dark:text-purple-400 italic">
                                    <FormattedMessage id="jobDetailPage.noRemoteness" defaultMessage="Not specified" description="Fallback text when work model/remoteness is not specified" />
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Application Deadline / Estimated Expiry */}
                    <ApplicationDeadlineSection
                      formattedApplicationDate={formattedApplicationDate}
                      formattedEstimatedExpiryDate={formattedEstimatedExpiryDate}
                      formattedPostedDate={formattedPostedDate}
                    />

                    {/* Secondary Job Details - Unified Grid Layout */}
                    {(normalisedRoleLevel || peopleManagementTier || jobType || minimumYearsOfExperience || visaSponsor) && (
                      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {normalisedRoleLevel && (
                          <div className="p-4 rounded-2xl bg-gray-50/80 dark:bg-gray-800/40 border border-gray-200/60 dark:border-gray-700/60 shadow-sm">
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.roleLevel" defaultMessage="Role Level" description="Section label for job role level (junior, mid, senior, etc.)" />
                            </div>
                            <div className="text-base font-semibold text-gray-700 dark:text-gray-300">
                              {fixMojibake(normalisedRoleLevel ?? '')}
                            </div>
                          </div>
                        )}

                        {peopleManagementTier && (
                          <div className="p-4 rounded-2xl bg-gray-50/80 dark:bg-gray-800/40 border border-gray-200/60 dark:border-gray-700/60 shadow-sm">
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.peopleManagementTier" defaultMessage="Management Tier" description="Section label for people management responsibility level" />
                            </div>
                            <div className="text-base font-semibold text-gray-700 dark:text-gray-300">
                              {fixMojibake(peopleManagementTier ?? '')}
                            </div>
                          </div>
                        )}

                        {jobType && (
                          <div className="p-4 rounded-2xl bg-gray-50/80 dark:bg-gray-800/40 border border-gray-200/60 dark:border-gray-700/60 shadow-sm">
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.jobType" defaultMessage="Job Type" description="Section label for job type (full-time, part-time, contract, etc.)" />
                            </div>
                            <div className="text-base font-semibold text-gray-700 dark:text-gray-300">
                              {fixMojibake(jobType ?? '')}
                            </div>
                          </div>
                        )}

                        {minimumYearsOfExperience != null && minimumYearsOfExperience > 0 && (
                          <div className="p-4 rounded-2xl bg-gray-50/80 dark:bg-gray-800/40 border border-gray-200/60 dark:border-gray-700/60 shadow-sm">
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.experience" defaultMessage="Experience" description="Section label for required years of experience" />
                            </div>
                            <div className="text-base font-semibold text-gray-700 dark:text-gray-300">
                              <FormattedMessage
                                id="jobDetailPage.minimumYearsOfExperience"
                                defaultMessage="{years} {years, plural, one {year} other {years}}"
                                values={{ years: minimumYearsOfExperience }}
                              />
                            </div>
                          </div>
                        )}

                        {visaSponsor && (
                          <div className="p-4 rounded-2xl bg-blue-50/60 dark:bg-blue-900/20 border border-blue-200/60 dark:border-blue-800/60 shadow-sm">
                            <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1.5 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.visa" defaultMessage="Visa" description="Section label for visa sponsorship information" />
                            </div>
                            <div className="text-base font-semibold text-blue-700 dark:text-blue-300">
                              <FormattedMessage
                                id="jobDetailPage.visaSponsor"
                                defaultMessage="✓ Sponsorship available"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Match Score Details */}
                    {isAuthenticated && displayJob && (() => {
                      const matchScore = calculateJobMatch(displayJob, userProfile);
                      if (!matchScore) return null;
                      return <MatchScoreBreakdown matchScore={matchScore} />;
                    })()}

                    {/* Dates Row */}
                    <div className="flex flex-row justify-between items-center mb-1 mt-4">
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {formattedPostedDate && (
                          <FormattedMessage id="hit.postedDate.detail" defaultMessage="Posted: {date}" values={{ date: formattedPostedDate }} description="Job posting date shown on job detail page" />
                        )}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {formattedApplicationDate && (
                          <FormattedMessage
                            id="hit.applicationDeadline.detail"
                            defaultMessage="Deadline: {date}"
                            description="Application deadline shown on job detail page"
                            values={{ date: formattedApplicationDate }}
                          />
                        )}
                      </span>
                    </div>

                    {/* Languages and Specialisations - Unified Card Layout */}
                    {((requiredLanguages && requiredLanguages.length > 0) || (optionalLanguages && optionalLanguages.length > 0) || (specialisations && specialisations.length > 0)) && (
                      <div className="mb-6 p-4 rounded-2xl bg-gray-50/80 dark:bg-gray-800/40 border border-gray-200/60 dark:border-gray-700/60 shadow-sm">
                        {/* Required Languages */}
                        {requiredLanguages && requiredLanguages.length > 0 && (
                          <div className="mb-4">
                            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.requiredLanguages" defaultMessage="Required Languages" description="Section heading for list of required languages for the job" />
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {requiredLanguages.map((lang: string, idx: number) => (
                                <span key={lang + idx} className="px-3 py-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-sm font-medium border border-blue-200 dark:border-blue-700">
                                  {lang}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Optional Languages */}
                        {optionalLanguages && optionalLanguages.length > 0 && (
                          <div className="mb-4">
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.optionalLanguages" defaultMessage="Optional Languages" description="Section heading for list of optional/preferred languages for the job" />
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {optionalLanguages.map((lang: string, idx: number) => (
                                <span key={lang + idx} className="px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-400 text-sm font-medium border border-gray-200 dark:border-gray-600">
                                  {lang}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Specialisations */}
                        {specialisations && specialisations.length > 0 && (
                          <div>
                            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.specialisations" defaultMessage="Specialisations" description="Section heading for job specializations or focus areas" />
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {specialisations.map((spec: string, idx: number) => (
                                <span key={spec + idx} className="px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700/60 text-gray-700 dark:text-gray-300 text-sm font-medium border border-gray-200 dark:border-gray-600">
                                  {spec}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Job Description */}
                    {description && (
                      <div className="my-4">
                        <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">
                          <FormattedMessage id="jobDetailPage.description" defaultMessage="Job Description" description="Section heading for full job description text" />
                        </h3>
                        <p className="text-gray-700 dark:text-gray-200 text-base whitespace-pre-line">{description}</p>
                      </div>
                    )}

                    {/* Apply Button */}
                    <ApplyButton
                      applyUrl={applyUrl}
                      isApplied={isApplied}
                      displayJob={displayJob}
                      applyBtnStyle={applyBtnStyle}
                      applyBtnHoverStyle={applyBtnHoverStyle}
                      effectiveBrandColour={effectiveBrandColour}
                      onSetApplied={setAppliedJobState}
                    />

                    {/* Contact Email Link */}
                    <ContactEmailSection
                      contactEmail={contactEmail}
                      showContactEmail={showContactEmail}
                      onShowContactEmail={() => setShowContactEmail(true)}
                    />
                  </div>

                  <CompanySection
                    displayJob={displayJob}
                    companyId={companyId}
                    companyIndustry={companyIndustry}
                    companyAbout={companyAbout}
                    companyContactDetails={companyContactDetails}
                    companyRevenue={companyRevenue}
                    companyProfit={companyProfit}
                    companyProfitMargin={companyProfitMargin}
                    companyMonthlyCounts={companyMonthlyCounts}
                    companyHeadCountMin={companyHeadCountMin}
                    companyHeadCountMax={companyHeadCountMax}
                    companyHeadcountRangeMin={companyHeadcountRangeMin}
                    companyHeadcountRangeMax={companyHeadcountRangeMax}
                    companyHQInFinland={companyHQInFinland}
                    companyVideo={companyVideo}
                    linkedinUrl={linkedinUrl}
                    facebookUrl={facebookUrl}
                    instagramUrl={instagramUrl}
                    youtubeUrl={youtubeUrl}
                    tiktokUrl={tiktokUrl}
                    registryMainBusinessLineDescriptionEn={registryMainBusinessLineDescriptionEn}
                    registryMainBusinessLineDescriptionFi={registryMainBusinessLineDescriptionFi}
                    registryMainBusinessLineDescriptionSv={registryMainBusinessLineDescriptionSv}
                    registryStreet={registryStreet}
                    registryCity={registryCity}
                    registryPostCode={registryPostCode}
                    registryBuildingNumber={registryBuildingNumber}
                    address={address}
                    address_fi={address_fi}
                    address_sv={address_sv}
                    businessID={businessID}
                    registryBusinessId={registryBusinessId}
                    companyGeocoordinates={companyGeocoordinates}
                    currentLocaleTrends={currentLocaleTrends}
                    jobLocale={jobLocale}
                    brandColour={effectiveBrandColour}
                    useBrandColor={useBrandColor}
                    hasAnyCharts={hasAnyCharts}
                    chartsCollapsed={chartsCollapsed}
                    onToggleChartsCollapse={toggleChartsCollapse}
                    companyRef={companyRef}
                  />

                  {/* JobDetailsSection - Reuse same component for both desktop and mobile */}
                  <JobDetailsSection
                    displayJob={displayJob}
                    specialisations={specialisations}
                    optionalLanguages={optionalLanguages}
                    perks={perks}
                    originalJobText={originalJobText}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    activeTabStyle={activeTabStyle}
                    jobDetailsRef={jobDetailsRef}
                  />

                  {/* Mobile-specific additional fields */}
                  {salaryDetails && (
                    <div className="lg:hidden mt-6">
                      <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">
                        <FormattedMessage
                          id="jobDetailPage.salaryDetails"
                          defaultMessage="Salary Details"
                          description="Section heading for detailed salary information and compensation breakdown"
                        />
                      </h3>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{fixMojibake(salaryDetails)}</p>
                    </div>
                  )}
                </div>
              </div>
            </main>
          </div>

          {/* Footer - Full width outside main content container */}
          <Footer />

          <MobileBottomNavigation
            activeSection={activeSection}
            onSectionChange={handleScrollToSection}
          />
        </div>
    );
  };

export default JobDetailPage;